import {Component, OnDestroy, OnInit} from '@angular/core';
import {BatchDisplayStatus, SettlementSearchCriteria} from 'web-angular-client';
import {BatchStatusUtil} from '../../../../sdk/util/batchStatusUtil';
import {SearchService} from '../../../../sdk/component/search/search.service';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {SettlementSearchCriteriaImpl} from '../../../../sdk/component/search/settlement-search-criteria';
import {SearchFilterBase} from '../../../../sdk/component/search/search-filter-base';
import {Helper} from '../../../../sdk/util/helper';
import {FilterPersistenceService} from '../../../../sdk/component/search/filter-persistence.service';
import {ActivatedRoute} from '@angular/router';
import {formatLocalYMD, toUtcIsoAtLocalEndOfDay, toUtcIsoAtLocalStartOfDay} from '../../../../sdk/util/searchUtil';

@Component({
    selector: 'app-batch-search',
    templateUrl: './batch-search.component.html',
    styleUrls: ['./batch-search.component.scss'],
    standalone: false
})
export class BatchSearchComponent extends SearchFilterBase implements OnInit, OnDestroy
{
    form: UntypedFormGroup;
    private BATCH_NUMBER: string = 'batchNumber';
    private TOTAL_TRANSACTIONS_LOWER: string = 'totalTransactionsLower';
    private TOTAL_TRANSACTIONS_UPPER: string = 'totalTransactionsUpper';
    private TOTAL_AMOUNT_LOWER: string = 'totalAmountLower';
    private TOTAL_AMOUNT_UPPER: string = 'totalAmountUpper';
    private TOTAL_TRANSACTION_FEES_INCLVAT_LOWER: string = 'totalTransactionFeesInclVatLower';
    private TOTAL_TRANSACTION_FEES_INCLVAT_UPPER: string = 'totalTransactionFeesInclVatUpper';
    private SETTLEMENT_FEE_INCLVAT_LOWER: string = 'settlementFeeInclVatLower';
    private SETTLEMENT_FEE_INCLVAT_UPPER: string = 'settlementFeeInclVatUpper';
    private TOTAL_AMOUNT_SETTLED_LOWER: string = 'totalAmountSettledLower';
    private TOTAL_AMOUNT_SETTLED_UPPER: string = 'totalAmountSettledUpper';
    private UPDATED_LOWER: string = 'updatedLower';
    private UPDATED_UPPER: string = 'updatedUpper';
    private DISPLAY_STATUS: string = 'displayStatus';
    private BATCH = 'batch';
    private NUM_PAYMENTS = 'numPayments';
    private TOTAL_AMOUNT = 'totalAmount';
    private TOTAL_TRANSACTION_FEES_INCL_VAT = 'totalTransactionFeesInclVat';
    private SETTLEMENT_FEE_INCL_VAT = 'settlementFeeInclVat';
    private SETTLEMENT_AMOUNT = 'settlementAmount';
    private UPDATED = 'updated';
    private STATUS = 'status';
    private settlementSearchCriteria: SettlementSearchCriteria = new SettlementSearchCriteriaImpl();

    constructor(
        protected searchDataService: SearchService,
        protected filterPersistenceService: FilterPersistenceService,
        protected route: ActivatedRoute,
        private batchStatusUtil: BatchStatusUtil,
        private formBuilder: UntypedFormBuilder,
        protected ngxLoader: NgxUiLoaderService)
    {
        super(searchDataService, ngxLoader, filterPersistenceService, route);
    }

    get activeTab(): string
    {
        return this._activeTab;
    }

    get totalTransactionsUpper()
    {
        return this.form.get(this.TOTAL_TRANSACTIONS_UPPER).value;
    }

    get totalTransactionsLower()
    {
        return this.form.get(this.TOTAL_TRANSACTIONS_LOWER).value;
    }

    get totalAmountUpper()
    {
        return this.form.get(this.TOTAL_AMOUNT_UPPER).value;
    }

    get totalAmountLower()
    {
        return this.form.get(this.TOTAL_AMOUNT_LOWER).value;
    }

    get totalTransactionFeesInclVatUpper()
    {
        return this.form.get(this.TOTAL_TRANSACTION_FEES_INCLVAT_UPPER).value;
    }

    set totalTransactionFeesInclVatUpper(val: number)
    {
        this.settlementSearchCriteria.totalTransactionFeesInclVatUpper = val;
    }

    get totalTransactionFeesInclVatLower()
    {
        return this.form.get(this.TOTAL_TRANSACTION_FEES_INCLVAT_LOWER).value;
    }

    set totalTransactionFeesInclVatLower(val: number)
    {
        this.settlementSearchCriteria.totalTransactionFeesInclVatLower = val;
    }

    get settlementFeeInclVatUpper()
    {
        return this.form.get(this.SETTLEMENT_FEE_INCLVAT_UPPER).value;
    }

    set settlementFeeInclVatUpper(val: number)
    {
        this.settlementSearchCriteria.settlementFeeInclVatUpper = val;
    }

    get settlementFeeInclVatLower()
    {
        return this.form.get(this.SETTLEMENT_FEE_INCLVAT_LOWER).value;
    }

    set settlementFeeInclVatLower(val: number)
    {
        this.settlementSearchCriteria.settlementFeeInclVatLower = val;
    }

    get totalAmountSettledUpper()
    {
        return this.form.get(this.TOTAL_AMOUNT_SETTLED_UPPER).value;
    }

    set totalAmountSettledUpper(val: number)
    {
        this.settlementSearchCriteria.totalAmountSettledUpper = val;
    }

    get totalAmountSettledLower()
    {
        return this.form.get(this.TOTAL_AMOUNT_SETTLED_LOWER).value;
    }

    set totalAmountSettledLower(val: number)
    {
        this.settlementSearchCriteria.totalAmountSettledLower = val;
    }

    get updatedUpper()
    {
        let date: Date =
            this.settlementSearchCriteria.updatedUpper ?
                new Date(this.settlementSearchCriteria.updatedUpper) :
                new Date();

        return formatLocalYMD(date);
    }

    get updatedLower()
    {
        return this.settlementSearchCriteria.updatedLower ?
            formatLocalYMD(new Date(this.settlementSearchCriteria.updatedLower)) :
            null;
    }

    get tabActive(): boolean
    {
        return !!this.activeTab;
    }

    get displayStatus(): string
    {
        return this.settlementSearchCriteria.displayStatus.valueOf();
    }

    get statusOptions()
    {
        return [
            BatchDisplayStatus.ANY,
            BatchDisplayStatus.OPEN,
            BatchDisplayStatus.CLOSED,
            BatchDisplayStatus.SETTLED
        ];
    }

    ngOnInit(): void
    {
        this.form = this.formBuilder.group({
            batchNumber: [''],
            totalTransactionsLower: [''],
            totalTransactionsUpper: [''],
            totalAmountLower: [''],
            totalAmountUpper: [''],
            totalTransactionFeesInclVatLower: [''],
            totalTransactionFeesInclVatUpper: [''],
            settlementFeeInclVatLower: [''],
            settlementFeeInclVatUpper: [''],
            totalAmountSettledLower: [''],
            totalAmountSettledUpper: [''],
            updatedLower: [''],
            updatedUpper: [''],
            displayStatus: [BatchDisplayStatus.ANY]
        });

        this.initializeSearchPage();
    }

    ngOnDestroy(): void
    {
        clearInterval(this.interval);
    }

    setSearchCriteria(event: any)
    {
        let inputId = event.target.id;
        let val = event.target.value;
        this.setSettlementSearchCriteria(inputId, val);
    }

    refresh()
    {
        this.searchDataService.searchableCriteria = this.settlementSearchCriteria;
        this.saveCurrentFilters();
        this.searchDataService.refreshSearch({
            ...this.settlementSearchCriteria,
            totalAmountLower: Helper.convertToMinorDenomination(this.totalAmountLower),
            totalAmountUpper: Helper.convertToMinorDenomination(this.totalAmountUpper),
            totalAmountSettledLower: Helper.convertToMinorDenomination(this.totalAmountSettledLower),
            totalAmountSettledUpper: Helper.convertToMinorDenomination(this.totalAmountSettledUpper)
        });
    }

    getSearchCriteria(): any
    {
        return this.settlementSearchCriteria;
    }

    setSettlementSearchCriteria(id: string, val: any)
    {
        switch (id)
        {
            case this.BATCH_NUMBER:
                this.settlementSearchCriteria.batchNumber = <number> val;
                break;
            case this.TOTAL_TRANSACTIONS_LOWER:
                this.settlementSearchCriteria.totalTransactionsLower = <number> val;
                break;
            case this.TOTAL_TRANSACTIONS_UPPER:
                this.settlementSearchCriteria.totalTransactionsUpper = <number> val;
                break;
            case this.TOTAL_AMOUNT_LOWER:
                this.settlementSearchCriteria.totalAmountLower = val;
                break;
            case this.TOTAL_AMOUNT_UPPER:
                this.settlementSearchCriteria.totalAmountUpper = val;
                break;
            case this.TOTAL_TRANSACTION_FEES_INCLVAT_LOWER:
                this.settlementSearchCriteria.totalTransactionFeesInclVatLower = <number> val;
                break;
            case this.TOTAL_TRANSACTION_FEES_INCLVAT_UPPER:
                this.settlementSearchCriteria.totalTransactionFeesInclVatUpper = <number> val;
                break;
            case this.SETTLEMENT_FEE_INCLVAT_LOWER:
                this.settlementSearchCriteria.settlementFeeInclVatLower = <number> val;
                break;
            case this.SETTLEMENT_FEE_INCLVAT_UPPER:
                this.settlementSearchCriteria.settlementFeeInclVatUpper = <number> val;
                break;
            case this.TOTAL_AMOUNT_SETTLED_LOWER:
                this.settlementSearchCriteria.totalAmountSettledLower = val;
                break;
            case this.TOTAL_AMOUNT_SETTLED_UPPER:
                this.settlementSearchCriteria.totalAmountSettledUpper = val;
                break;
            case this.UPDATED_LOWER:
                this.settlementSearchCriteria.updatedLower = toUtcIsoAtLocalStartOfDay(val);
                break;
            case this.UPDATED_UPPER:
                this.settlementSearchCriteria.updatedUpper = toUtcIsoAtLocalEndOfDay(val);
                break;
            case this.DISPLAY_STATUS:
                this.settlementSearchCriteria.status = this.batchStatusUtil.getBatchStatusFromDescription(<string> val);
                this.settlementSearchCriteria.displayStatus = this.batchStatusUtil.getDisplayStatusFromDescription(<string> val);
                break;
        }
    }

    getTabContent(event: any)
    {
        this._activeTab = event.target.id.toString();
        this.saveCurrentFilters();
    }

    clear(activeTab: string)
    {
        switch (activeTab)
        {
            case this.BATCH:
                this.settlementSearchCriteria.batchNumber = null;
                this.form.get('batchNumber').reset();
                break;
            case this.NUM_PAYMENTS:
                this.settlementSearchCriteria.totalTransactionsUpper = null;
                this.settlementSearchCriteria.totalTransactionsLower = null;
                this.form.get('totalTransactionsUpper').reset();
                this.form.get('totalTransactionsLower').reset();
                break;
            case this.TOTAL_AMOUNT:
                this.settlementSearchCriteria.totalAmountLower = null;
                this.settlementSearchCriteria.totalAmountUpper = null;
                this.form.get('totalAmountLower').reset();
                this.form.get('totalAmountUpper').reset();
                break;
            case this.TOTAL_TRANSACTION_FEES_INCL_VAT:
                this.settlementSearchCriteria.totalTransactionFeesInclVatLower = null;
                this.settlementSearchCriteria.totalTransactionFeesInclVatUpper = null;
                this.form.get('totalTransactionFeesInclVatLower').reset();
                this.form.get('totalTransactionFeesInclVatUpper').reset();
                break;
            case this.SETTLEMENT_FEE_INCL_VAT:
                this.settlementSearchCriteria.settlementFeeInclVatLower = null;
                this.settlementSearchCriteria.settlementFeeInclVatUpper = null;
                this.form.get('settlementFeeInclVatLower').reset();
                this.form.get('settlementFeeInclVatUpper').reset();
                break;
            case this.SETTLEMENT_AMOUNT:
                this.settlementSearchCriteria.totalAmountSettledLower = null;
                this.settlementSearchCriteria.totalAmountSettledUpper = null;
                this.form.get('totalAmountSettledLower').reset();
                this.form.get('totalAmountSettledUpper').reset();
                break;
            case this.UPDATED:
                this.settlementSearchCriteria.updatedLower = null;
                this.settlementSearchCriteria.updatedUpper = null;
                this.clearDateInputField(this.form, 'updatedLower');
                this.clearDateInputField(this.form, 'updatedUpper');
                break;
            case this.STATUS:
                this.settlementSearchCriteria.status = [];
                this.settlementSearchCriteria.displayStatus = this.batchStatusUtil.getDisplayStatusFromDescription('Any');
                this.form.get('batchStatus').reset('Any');
                break;
        }
        this.search();
    }

    clearAll()
    {
        this.settlementSearchCriteria = new SettlementSearchCriteriaImpl();
        this.form.reset(this.settlementSearchCriteria);
        this.search();
    }

    disableClearAll()
    {
        return !this.form.dirty;
    }

    disableClear(activeTab: string): boolean
    {
        switch (activeTab)
        {
            case this.BATCH:
                return !this.form.get(this.BATCH_NUMBER).dirty;
            case this.NUM_PAYMENTS:
                return !(this.form.get(this.TOTAL_TRANSACTIONS_UPPER).dirty || this.form.get(this.TOTAL_TRANSACTIONS_LOWER).dirty);
            case this.TOTAL_AMOUNT:
                return !(this.form.get(this.TOTAL_TRANSACTIONS_UPPER).dirty || this.form.get(this.TOTAL_TRANSACTIONS_LOWER).dirty);
            case this.TOTAL_TRANSACTION_FEES_INCL_VAT:
                return !(this.form.get(this.TOTAL_TRANSACTION_FEES_INCLVAT_UPPER).dirty || this.form.get(this.TOTAL_TRANSACTION_FEES_INCLVAT_LOWER).dirty);
            case this.SETTLEMENT_FEE_INCL_VAT:
                return !(this.form.get(this.SETTLEMENT_FEE_INCLVAT_UPPER).dirty || this.form.get(this.SETTLEMENT_FEE_INCLVAT_LOWER).dirty);
            case this.SETTLEMENT_AMOUNT:
                return !(this.form.get(this.TOTAL_AMOUNT_SETTLED_UPPER).dirty || this.form.get(this.TOTAL_AMOUNT_SETTLED_LOWER).dirty);
            case this.UPDATED:
                return !(this.form.get(this.UPDATED_UPPER).dirty || this.form.get(this.UPDATED_LOWER).dirty);
            case this.STATUS:
                return !this.form.get(this.DISPLAY_STATUS).dirty;
            default:
                return true;
        }
    }

    setFilters(): void
    {
        this.applyFilters(
            this.form,
            (criteria: SettlementSearchCriteria) => this.settlementSearchCriteria = criteria,
            null
        );
    }
}